import React, { useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faBookOpen,
  faCirclePlay,
  faCodeBranch,
  faEnvelope,
  faMagnifyingGlass,
  faMessage,
  faMobileScreenButton,
} from "@fortawesome/free-solid-svg-icons";
import WorkflowInbounds from "../Inbounds/WorkflowInbounds";

const campaignVideos = ["Create a Campaign", "Managing Campaign responses"];

export default function CampaignInformation() {
  const [showCampaignLibrary, setShowCampaignLibrary] = useState(false);
  const [videoSearchTerm, setVideoSearchTerm] = useState("");
  const videoSearchInputRef = useRef(null);
  const normalisedVideoSearchTerm = videoSearchTerm.trim().toLowerCase();
  const filteredCampaignVideos = campaignVideos.filter((video) =>
    video.toLowerCase().includes(normalisedVideoSearchTerm)
  );

  return (
    <main className="campaign-information container-fluid py-4 py-lg-5">
      <div className="row g-4">
        <section className="col-lg-8" aria-labelledby="campaign-overview-heading">
          <div className="campaign-information__card card border-0 rounded-4 p-4 p-lg-5">
            <h2 id="campaign-overview-heading" className="h3 fw-bold mb-3">About Campaign</h2>
            <p className="text-secondary">
              Give people a direct way to contact the right department by SMS, email, or voice.
              Campaigns capture inbound messages and route them into the response process that
              works best for your organisation.
            </p>
            <div className="row g-3 mt-1">
              <Feature icon={faMessage} title="Inbound communication">
                Create a dedicated contact point for people to message your team.
              </Feature>
              <Feature icon={faMobileScreenButton} title="Flexible channels">
                Receive SMS, email, and voice messages through the same campaign workflow.
              </Feature>
              <Feature icon={faCodeBranch} title="Connected processes">
                Send messages to an automated workflow, API, or a tailored response process.
              </Feature>
              <Feature icon={faEnvelope} title="Better service">
                Make it simple for customers and stakeholders to request help or information.
              </Feature>
            </div>
            <div className="mt-4">
              <button
                type="button"
                className="btn bg-colored-reverse rounded-pill text-white px-4"
                onClick={() => setShowCampaignLibrary(true)}
              >
                Create a campaign <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </button>
            </div>
          </div>
        </section>
        <aside className="col-lg-4" aria-labelledby="campaign-help-heading">
          <div className="campaign-information__card card border-0 rounded-4 p-4 p-lg-5 h-100">
            <div className="d-flex align-items-center gap-2 mb-3">
              <FontAwesomeIcon className="text-purple fs-3" icon={faBookOpen} aria-hidden="true" />
              <h2 id="campaign-help-heading" className="h3 fw-bold mb-0">Campaign help</h2>
            </div>
            <p className="text-secondary">
              Visit the help centre for step-by-step guidance and answers to common Campaign
              questions.
            </p>
            <a
              href="https://help.uatboomcomms.com/"
              target="_blank"
              rel="noreferrer"
              className="campaign-information__link fw-bold text-decoration-none"
            >
              Open Campaign help <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </a>
            <div className="campaign-information__videos mt-4 pt-4">
              <div className="d-flex align-items-start gap-2 mb-3">
                <FontAwesomeIcon className="text-purple fs-4 mt-1" icon={faCirclePlay} aria-hidden="true" />
                <div>
                  <h2 id="campaign-videos-heading" className="h3 fw-bold mb-1">
                    Campaign videos
                  </h2>
                  <p className="small text-secondary mb-0">
                    Learn the key steps for creating and managing Campaigns.
                  </p>
                </div>
              </div>
              <form
                className="campaign-information__video-search input-group input-group-sm mb-3"
                onSubmit={(event) => event.preventDefault()}
              >
                <label className="visually-hidden" htmlFor="campaign-video-search">
                  Search campaign videos
                </label>
                <input
                  ref={videoSearchInputRef}
                  id="campaign-video-search"
                  type="search"
                  className="form-control form-control-sm"
                  placeholder="Search campaign videos"
                  value={videoSearchTerm}
                  onChange={(event) => setVideoSearchTerm(event.target.value)}
                />
                <button
                  type="submit"
                  className="campaign-information__video-search-button btn"
                  aria-label="Search campaign videos"
                  onClick={() => videoSearchInputRef.current?.focus()}
                >
                  <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" />
                </button>
              </form>
              <ul className="campaign-information__video-list list-unstyled mb-0">
                {filteredCampaignVideos.map((video) => (
                  <li key={video}>
                    <a
                      href="https://help.uatboomcomms.com/"
                      target="_blank"
                      rel="noreferrer"
                      className="campaign-information__video-link d-flex align-items-center justify-content-between rounded-3 p-3 text-decoration-none"
                    >
                      <span>{video}</span>
                      <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                    </a>
                  </li>
                ))}
              </ul>
              {normalisedVideoSearchTerm && filteredCampaignVideos.length === 0 && (
                <p className="campaign-information__video-search-empty mb-0">No campaign videos found.</p>
              )}
            </div>
          </div>
        </aside>
        {showCampaignLibrary && (
          <section className="col-12 mt-4" aria-label="Campaign Library">
            <WorkflowInbounds />
          </section>
        )}
      </div>
    </main>
  );
}

function Feature({ icon, title, children }) {
  return (
    <div className="col-md-6">
      <div className="campaign-information__feature h-100 rounded-3 p-3">
        <FontAwesomeIcon className="text-purple me-2" icon={icon} aria-hidden="true" />
        <strong>{title}</strong>
        <div className="small text-secondary mt-2">{children}</div>
      </div>
    </div>
  );
}